import React from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { elevate } from '@fs/zion-ui/elevation'
import { NoticeQuestion, MenuMore } from '@fs/zion-icon'
import { useLocation, Switch, Route } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { Body2, Button, IconButton, colors, MenuOverlay, ListItem, useAtSize } from '@fs/zion-ui'
import { boolAttr } from '@fs/zion-frontend-friends'
import sendAnalytics from '../../services/analytics-helper'
import FSLogo from './FSLogo'
import { getExternalBaseUrl } from '../../services/url-service'
import { DataContext, useDataContext } from '../../services/data-service'
import AncestorPageHeader from '../AncestorPage/AncestorPageHeader'
import { ANCESTOR_PAGE_PATHS } from '../../App'

const HelpOverlay = React.lazy(() => import('@fs/zion-ui/help-overlay'))

const shadowCss = css`
  position: relative;
  ${elevate(4)}
  z-index: 2;
  &[sticky] {
    z-index: 2;
    position: sticky;
    top: -60px;
  }
`

const headerCss = css`
  align-items: center;
  background: ${colors.background.primary};
  display: flex;
  height: 60px;
  justify-content: space-between;
  padding: 0 24px;

  &[is-mobile] {
    padding: 0 12px;
  }
`

const nameCss = css`
  margin: 13px 5px 0 5px;
`

const navButtonsCss = css`
  display: inline-flex;
  align-items: center;

  a {
    margin-left: 8px;
    display: inline-flex;
    align-items: center;
    height: 42px;
  }
`

export default function AncestorsHeader(): JSX.Element {
  const {
    store: { CF = false },
  } = useDataContext()
  const [t] = useTranslation()
  const user = useUser()
  const pageLocation = useLocation()
  const signedIn = user.signedIn
  const externalBaseUrl = getExternalBaseUrl() || 'https://www.familysearch.org'
  const helpActionUrl = `${externalBaseUrl}/help/main`
  const identityUrl = window.location.href.includes('localhost') ? window.location.origin : getExternalBaseUrl()
  // We use useLocation to keep our url state current. window.location.href doesn't stay current.
  const returnUrl = encodeURIComponent(window.location.origin + pageLocation.pathname)

  const atSize = useAtSize()
  const isMobile = atSize({ default: true, md: false })
  const headerButtons = atSize({
    default: (
      <MenuOverlay menuButton={<IconButton emphasis="low" Icon={MenuMore} />} aria-label="Submenu">
        <ListItem
          primaryText={t('common-ui:sign-in.action', 'Sign in')}
          external
          data-testid="go-to-log-in"
          // TODO: Switch to the "to" prop and remove the window.location.href below once this jira is resolved: https://fhjira.churchofjesuschrist.org/browse/ZIONUI-720?filter=-2
          // to={`${identityUrl}/auth/familysearch/login?returnUrl=${returnUrl}`}
          onClick={() => {
            sendAnalytics({ link_page: 'Ancestors', link_name: 'Log In' })
            window.location.href = `${identityUrl}/auth/familysearch/login?returnUrl=${returnUrl}`
          }}
        />
        <ListItem
          primaryText={t('ancestors.create-account.action', 'Create Account')}
          external
          data-testid="go-to-create-account"
          // TODO: Switch to the "to" prop and remove the window.location.href below once this jira is resolved: https://fhjira.churchofjesuschrist.org/browse/ZIONUI-720?filter=-2
          // to={`${identityUrl}/register/?returnUrl=${returnUrl}`}
          onClick={() => {
            sendAnalytics({ link_page: 'Ancestors', link_name: 'Create Account' })
            window.location.href = `${identityUrl}/register/?returnUrl=${returnUrl}`
          }}
        />
      </MenuOverlay>
    ),
    md: (
      <>
        <Button
          data-testid="go-to-log-in"
          emphasis="low"
          external
          to={`${identityUrl}/auth/familysearch/login?returnUrl=${returnUrl}`}
          onClick={() => {
            sendAnalytics({ link_page: 'Ancestors', link_name: 'Log In' })
          }}
        >
          {t('common-ui:sign-in.action', 'Sign in')}
        </Button>
        <Button
          data-testid="go-to-create-account"
          emphasis="medium"
          external
          to={`${identityUrl}/register/?returnUrl=${returnUrl}`}
          onClick={() => {
            sendAnalytics({ link_page: 'Ancestors', link_name: 'Create Account' })
          }}
        >
          {t('ancestors.create-account.action', 'Create Account')}
        </Button>
      </>
    ),
  })

  return (
    <div css={shadowCss}>
      <header css={headerCss} is-mobile={boolAttr(isMobile)}>
        <FSLogo restricted={CF} />
        {!CF && (
          <div css={navButtonsCss}>
            <IconButton
              emphasis="low"
              Icon={NoticeQuestion}
              data-testid="help-button"
              text={t('ancestors.help.action', 'Help')}
              name="help"
              onClick={() => {
                if (typeof document !== 'undefined') {
                  sendAnalytics({ link_page: 'Ancestors', link_name: 'Header: Help' })
                  document.dispatchEvent(
                    new CustomEvent('openHelpTray', { detail: { helpActionUrl, apiBaseUrl: externalBaseUrl } })
                  )
                }
              }}
            />
            {!signedIn && !user.userLoading ? (
              headerButtons
            ) : (
              <>
                {!user.userLoading && signedIn && (
                  <>
                    {!isMobile && (
                      <div css={nameCss}>
                        <Body2>{user?.displayName}</Body2>
                      </div>
                    )}
                    <Button
                      data-testid="go-to-sign-out"
                      emphasis="medium"
                      external
                      to={`${identityUrl}/auth/logout/?returnUrl=${returnUrl}`}
                      onClick={() => {
                        sendAnalytics({ link_page: 'Ancestors', link_name: 'Log out' })
                      }}
                    >
                      {t('ancestors.sign-out.action', 'Sign out')}
                    </Button>
                  </>
                )}
              </>
            )}
            <HelpOverlay apiBaseUrl={externalBaseUrl} />
          </div>
        )}
      </header>
      <div>
        <Switch>
          <Route exact path={ANCESTOR_PAGE_PATHS}>
            <DataContext>
              <AncestorPageHeader />
            </DataContext>
          </Route>
        </Switch>
      </div>
    </div>
  )
}
